import {
  AnimatedCounter,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { ReactElement } from 'react';

interface StatusData {
  name: string;
  value: number;
  color: string;
}

interface OrderStatusChartProps {
  data: StatusData[];
  total: number;
  loading?: boolean;
}

const CHART_COLORS = {
  Active: 'var(--primary)',
  Pending: 'var(--warning)',
  'Completed Today': 'var(--success)',
  Overdue: 'var(--danger)',
} as Record<string, string>;

function GlassTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { name: string; value: number; payload: { color: string } }[];
}): ReactElement | null {
  if (
    active !== true ||
    payload === undefined ||
    payload.length === 0 ||
    payload[0] === undefined
  ) {
    return null;
  }
  const entry = payload[0];
  return (
    <div className="glass-strong rounded-lg px-3 py-2 text-xs shadow-[var(--glass-shadow)]">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: entry.payload.color }} />
        <span className="text-text font-medium">{entry.name}</span>
        <span className="text-text-secondary ml-1">{entry.value}</span>
      </div>
    </div>
  );
}

export function OrderStatusChart({ data, total, loading }: OrderStatusChartProps): ReactElement {
  if (loading === true) {
    return (
      <GlassCard className="h-full">
        <GlassCardHeader>
          <SkeletonLoader variant="text" width="60%" />
        </GlassCardHeader>
        <GlassCardContent>
          <SkeletonLoader variant="circle" width="140px" height="140px" className="mx-auto" />
        </GlassCardContent>
      </GlassCard>
    );
  }

  const coloredData = data.map((d) => ({
    ...d,
    color: CHART_COLORS[d.name] ?? '#6b7280',
  }));

  return (
    <GlassCard className="h-full">
      <GlassCardHeader>
        <h2 className="text-sm font-semibold text-text">Order Status</h2>
      </GlassCardHeader>
      <GlassCardContent className="flex flex-col items-center">
        {coloredData.length > 0 ? (
          <>
            <div className="relative w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={coloredData}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {coloredData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<GlassTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Center label */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <AnimatedCounter value={total} className="text-xl font-bold text-text" />
                <span className="text-xs text-text-tertiary">Total</span>
              </div>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 justify-center">
              {coloredData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: entry.color }}
                  />
                  <span className="text-text-secondary">{entry.name}</span>
                  <span className="font-medium text-text">{entry.value}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-40 flex items-center justify-center text-sm text-text-secondary">
            No data available
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
